import React from 'react';
import clsx from 'clsx';
import { Box } from '@mui/material';
import dayjs from 'dayjs';

import { Avatar, avatarType, Badge, badgeStyle, badgeType, Text } from '../../../../../../packages';

import { DomainPriorities } from '../../../../../../redux/modules/profile/types';
import { useStyles } from './MedicationCard.styles';
import { sentenceCase } from 'sentence-case';
import { GLOBAL_DATE_FORMAT } from '../../../../../../constants/CommonConstants';

const MedicationBottomCard = ({ medication, showBadge, detail }) => {
  const classes = useStyles();
  const {
    // tagMetaData: { rxDrugInfo },
    ...rest
  } = medication;

  const priority = rest.priority || rest.importanceLevel;

  const renderBlock = (label, value) => {
    return (
      <Box flex={0.5} minWidth="250px">
        <Text className={classes.label}>{label}</Text>
        <Text className={clsx(classes.label, classes.value)}>{value}</Text>
      </Box>
    );
  };

  const renderContent = () => {
    return detail ? (
      <Box display="flex" flexDirection="column" gap="12px">
        <Box flex={1} display="flex" flexWrap="wrap" gap="12px">
          {renderBlock('Drug', medication?.externalMedicationName || 'Not specified')}
          {renderBlock(
            'Quantity',
            medication?.quantity
              ? `${medication.quantity} ${medication.dose}${Number(medication.quantity) > 1 ? 's' : ''}`
              : '',
          )}
          {renderBlock('Frequency', medication?.frequency || 'Not specified')}
          {renderBlock(
            'Duration',
            (medication?.duration && `${medication.duration} day${medication.duration > 1 ? 's' : ''}`) ||
              'Not specified',
          )}
          {renderBlock('Sig', medication?.sig || 'Not specified')}
          {renderBlock(
            'Dispense Amount',
            medication?.dispenseAmount
              ? `${medication.dispenseAmount} ${medication.dose}${
                  Number(medication.dispenseAmount) > 1 ? 's' : ''
                }`
              : '',
          )}
          {renderBlock('Refills', medication?.refills ? `${medication.refills}` : 0)}
        </Box>
        <Box display="flex" gap="12px">
          <Box flex={1} display="flex" flexWrap="wrap" gap="12px">
            {renderBlock(
              'Pharmacy',
              medication?.tagMetaData?.rxDrugInfo?.pharmacyName
                ? `${medication?.tagMetaData?.rxDrugInfo?.pharmacyName}, ${medication?.tagMetaData?.rxDrugInfo?.pharmacyCode}`
                : '',
            )}
            {renderBlock(
              'Assigned at',
              medication?.assignedAt
                ? `${dayjs(medication.assignedAt).format(`${GLOBAL_DATE_FORMAT} hh:mm a`)}`
                : '',
            )}
          </Box>
        </Box>
        {medication.pharmacyNotes && detail && (
          <Box flex={1}>{renderBlock('Pharmacy Notes', medication.pharmacyNotes)}</Box>
        )}
        {medication.notes && detail && <Box flex={1}>{renderBlock('Internal Notes', medication.notes)}</Box>}
      </Box>
    ) : null;
  };

  return (
    <Box className={classes.borderTop}>
      <Box className={clsx(classes.rowCenter, classes.flexStart)}>
        <Box display="flex" gap="12px" alignItems="center" flex={showBadge ? 1 : 0.3}>
          <Avatar
            src={medication.avatar}
            size={40}
            variant={avatarType.CIRCLE}
            name={medication.assignedBy}
          />
          <Box display="flex" gap="4px" flexDirection="column" justifyContent="flex-start">
            <Text className={classes.username}>By {medication.assignedBy}</Text>
            <Text className={classes.time}>{dayjs(medication.assignedAt).format('MMMM DD, YYYY')}</Text>
          </Box>
        </Box>
        {showBadge ? (
          <Badge
            variant={badgeType.FILLED}
            className={clsx(classes.badge, {
              [classes.badgeMisreported]: priority.name === DomainPriorities.MISREPORTED,
            })}
            style={
              (priority.name?.toUpperCase() === 'TO_BE_PRESCRIBED' ||
              priority.name?.toUpperCase() === 'E_PRESCRIPTION_FAILED'
                ? badgeStyle.CRITICAL
                : badgeStyle[priority.name?.toUpperCase()]) || badgeStyle.UNRELATED
            }
          >
            {sentenceCase(priority.name || '')}
          </Badge>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            className={clsx(classes.interference, {
              [classes.interferenceResolved]: medication.status === 'Resolved',
            })}
            flex="0.7"
          >
            {renderContent()}
          </Box>
        )}
      </Box>
      {showBadge && <Box className={classes.bottomBox}>{renderContent()}</Box>}
    </Box>
  );
};

export { MedicationBottomCard };
